import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ScanLine, AlertTriangle } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { CrudPage, type CrudColumn, type CrudField, type ExtraFormRenderer } from '@/components/crud/CrudPage';
import { BarcodeScanner, isCameraAvailable } from '@/components/materials/BarcodeScanner';
import { rpc } from '@/api/client';

interface TyreProfile {
  id: string;
  brand: string;
  type: string;
  compound: string;
}

interface Tyre {
  id: string;
  tyreProfileId: string;
  brand: string;
  type: string;
  nr?: string;
  compound?: string;
  discarded?: boolean;
  tyre_id_fl?: string;
  tyre_id_fr?: string;
  tyre_id_rl?: string;
  tyre_id_rr?: string;
  teamId: string;
}

const TYRE_ID_KEYS = ['tyre_id_fl', 'tyre_id_fr', 'tyre_id_rl', 'tyre_id_rr'] as const;

type TyreIdKey = typeof TYRE_ID_KEYS[number];

const TYRE_ID_LABEL_KEYS: Record<TyreIdKey, 'materials.tyreId.fl' | 'materials.tyreId.fr' | 'materials.tyreId.rl' | 'materials.tyreId.rr'> = {
  tyre_id_fl: 'materials.tyreId.fl',
  tyre_id_fr: 'materials.tyreId.fr',
  tyre_id_rl: 'materials.tyreId.rl',
  tyre_id_rr: 'materials.tyreId.rr',
};

function findCrossSetDuplicates(
  values: Record<string, string>,
  allItems: Tyre[],
  editItem: Tyre | null,
): Map<TyreIdKey, string> {
  const dupes = new Map<TyreIdKey, string>();
  for (const key of TYRE_ID_KEYS) {
    const val = values[key]?.trim();
    if (!val) continue;
    for (const tyre of allItems) {
      if (editItem && tyre.id === editItem.id) continue;
      for (const otherKey of TYRE_ID_KEYS) {
        if (tyre[otherKey] === val) {
          dupes.set(key, `${tyre.brand} ${tyre.type}`);
          break;
        }
      }
      if (dupes.has(key)) break;
    }
  }
  return dupes;
}

export function TyresTab({ teamId, readOnly }: { teamId: string; readOnly?: boolean }) {
  const { t } = useI18n();
  const [profiles, setProfiles] = useState<TyreProfile[]>([]);

  useEffect(() => {
    rpc<TyreProfile[]>('ListTyreProfiles', {}).then(setProfiles).catch(console.error);
  }, []);

  const allItemsRef = useRef<Tyre[]>([]);
  const editItemRef = useRef<Tyre | null>(null);

  const [crossSetDuplicates, setCrossSetDuplicates] = useState<Map<TyreIdKey, string>>(new Map());
  const confirmedSnapshotRef = useRef<Record<string, string> | null>(null);

  const columns: CrudColumn<Tyre>[] = [
    { key: 'brand', label: t('common.brand') },
    {
      key: 'type',
      label: t('common.type'),
      render: (ty) => (
        <span className="inline-block px-2.5 py-0.5 bg-accent-subtle border border-accent/20 rounded-full text-xs font-medium text-accent-hover tracking-wide">
          {ty.type}
        </span>
      ),
    },
    {
      key: 'compound',
      label: t('materials.tyre.compound'),
      render: (ty) => ty.compound
        ? <span className="text-sm text-text-primary">{ty.compound === 'dry' ? t('materials.tyre.compound.dry') : t('materials.tyre.compound.wet')}</span>
        : <span className="text-text-muted">—</span>,
    },
    {
      key: 'nr',
      label: t('common.nr'),
      className: 'w-[80px] text-text-secondary tabular-nums text-sm',
      render: (ty) => ty.nr || <span className="text-text-muted">—</span>,
    },
    {
      key: 'discarded',
      label: t('materials.tyre.discarded'),
      className: 'w-[100px]',
      render: (ty) => ty.discarded
        ? <span className="inline-block px-2.5 py-0.5 bg-red/15 border border-red/25 rounded-full text-xs font-medium text-red tracking-wide">✕</span>
        : <span className="text-text-muted">—</span>,
    },
  ];

  const fields: CrudField[] = [
    {
      key: 'tyreProfileId',
      label: t('materials.profile'),
      type: 'select',
      placeholder: t('materials.selectProfile'),
      options: profiles.map(p => {
        const compoundLabel = p.compound === 'dry' ? t('materials.tyre.compound.dry') : p.compound === 'wet' ? t('materials.tyre.compound.wet') : p.compound;
        return { value: p.id, label: `${p.brand} / ${p.type} (${compoundLabel})` };
      }),
    },
    { key: 'nr', label: t('common.nr'), placeholder: 'e.g. 1', info: t('common.nrInfo'), optional: true, defaultValue: (items) => String(items.length + 1) },
    {
      key: 'discarded',
      label: t('materials.tyre.discarded'),
      type: 'checkbox',
      defaultValue: () => 'false',
    },
  ];

  const renderExtraFormContent: ExtraFormRenderer<Tyre> = ({ values, setValue, allItems, editItem }) => {
    allItemsRef.current = allItems;
    editItemRef.current = editItem;

    return (
      <TyreIdSection
        values={values}
        setValue={setValue}
        crossSetDuplicates={crossSetDuplicates}
      />
    );
  };

  const handleAfterSave = useCallback(async (id: string, values: Record<string, string>) => {
    await rpc('UpsertTyreIds', {
      id,
      tyre_id_fl: values.tyre_id_fl || '',
      tyre_id_fr: values.tyre_id_fr || '',
      tyre_id_rl: values.tyre_id_rl || '',
      tyre_id_rr: values.tyre_id_rr || '',
    });
  }, []);

  const validateBeforeSave = useCallback((values: Record<string, string>): string | null => {
    const seen = new Set<string>();
    for (const key of TYRE_ID_KEYS) {
      const val = values[key]?.trim();
      if (!val) continue;
      if (seen.has(val)) {
        return t('materials.tyreId.duplicateInSet');
      }
      seen.add(val);
    }

    const dupes = findCrossSetDuplicates(values, allItemsRef.current, editItemRef.current);

    if (dupes.size === 0) {
      setCrossSetDuplicates(new Map());
      confirmedSnapshotRef.current = null;
      return null;
    }

    const snapshot = confirmedSnapshotRef.current;
    if (snapshot) {
      let allUnchanged = true;
      for (const key of dupes.keys()) {
        if ((values[key]?.trim() || '') !== (snapshot[key] || '')) {
          allUnchanged = false;
          break;
        }
      }
      if (allUnchanged) {
        setCrossSetDuplicates(new Map());
        confirmedSnapshotRef.current = null;
        return null;
      }
    }

    setCrossSetDuplicates(dupes);
    const newSnapshot: Record<string, string> = {};
    for (const key of dupes.keys()) {
      newSnapshot[key] = values[key]?.trim() || '';
    }
    confirmedSnapshotRef.current = newSnapshot;
    return '';
  }, [t]);

  return (
    <CrudPage<Tyre>
      title={t('materials.tab.tyres')}
      entityName={t('materials.entity.tyre')}
      emptyIcon="🏁"
      columns={columns}
      fields={fields}
      listMethod="ListTyres"
      createMethod="CreateTyre"
      updateMethod="UpdateTyre"
      deleteMethod="DeleteTyre"
      renderExtraFormContent={renderExtraFormContent}
      onAfterSave={handleAfterSave}
      validateBeforeSave={validateBeforeSave}
      listParams={{ teamId }}
      readOnly={readOnly}
      hiddenValues={{ teamId }}
    />
  );
}

/* -- Tire ID Section ---------------------------------------------------- */

interface TyreIdSectionProps {
  values: Record<string, string>;
  setValue: (key: string, val: string) => void;
  crossSetDuplicates: Map<TyreIdKey, string>;
  teamId?: string;
}

function TyreIdSection({ values, setValue, crossSetDuplicates }: TyreIdSectionProps) {
  const { t } = useI18n();
  const [scanningField, setScanningField] = useState<TyreIdKey | null>(null);
  const cameraAvailable = useMemo(() => isCameraAvailable(), []);

  const withinSetDuplicates = useMemo(() => {
    const dupes = new Set<string>();
    const seen = new Map<string, TyreIdKey>();
    for (const key of TYRE_ID_KEYS) {
      const val = values[key]?.trim();
      if (!val) continue;
      if (seen.has(val)) {
        dupes.add(key);
        dupes.add(seen.get(val)!);
      } else {
        seen.set(val, key);
      }
    }
    return dupes;
  }, [values]);

  const handleScanSuccess = (decodedText: string) => {
    if (scanningField) {
      setValue(scanningField, decodedText);
      setScanningField(null);
    }
  };

  return (
    <>
      {scanningField && (
        <BarcodeScanner
          onScanSuccess={handleScanSuccess}
          onClose={() => setScanningField(null)}
        />
      )}

      <div className="pt-3 mt-1 border-t border-border-default">
        <label className="text-xs font-medium text-text-muted block mb-3">
          {t('materials.tyreId.title')} <span className="text-text-muted font-normal">{t('common.optional')}</span>
        </label>
        <div className="grid grid-cols-1 gap-3">
          {TYRE_ID_KEYS.map(key => {
            const val = values[key]?.trim() || '';
            const isDuplInSet = withinSetDuplicates.has(key) && val !== '';
            const crossSetName = crossSetDuplicates.get(key);

            return (
              <div key={key}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-text-secondary w-6 shrink-0">
                    {t(TYRE_ID_LABEL_KEYS[key])}
                  </span>
                  <input
                    type="text"
                    value={values[key] ?? ''}
                    onChange={e => setValue(key, e.target.value)}
                    placeholder={t('materials.tyreId.placeholder')}
                    autoComplete="off"
                    className={`editor-input flex-1 ${isDuplInSet ? 'border-red!' : crossSetName ? 'border-yellow!' : ''}`}
                  />
                  {cameraAvailable && (
                    <button
                      type="button"
                      onClick={() => setScanningField(key)}
                      className="p-2 rounded-md border border-border-default text-text-secondary
                        hover:text-text-primary hover:border-border-hover transition-colors cursor-pointer
                        shrink-0"
                      title={t('materials.tyreId.scan')}
                    >
                      <ScanLine size={16} />
                    </button>
                  )}
                </div>
                {isDuplInSet && (
                  <p className="text-xs text-red mt-1 ml-8">
                    {t('materials.tyreId.duplicateInSet')}
                  </p>
                )}
                {crossSetName && !isDuplInSet && (
                  <p className="text-xs text-yellow mt-1 ml-8">
                    {t('materials.tyreId.duplicateInOtherSet', { id: val, tyre: crossSetName })}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {crossSetDuplicates.size > 0 && (
          <div className="mt-4 flex items-start gap-2.5 p-3 rounded-md bg-yellow/10 border border-yellow/25 text-sm text-yellow">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{t('materials.tyreId.crossSetInfo')}</span>
          </div>
        )}
      </div>
    </>
  );
}
